import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD302Query } from './hooks/useD302Query';
import type { ReportRow, UnitSummary } from '../../services/d302Api';
import './D302Form.css';

const reportTypeLabels: Record<string, string> = {
  order_no: '單據別',
  date: '日期別',
  product: '產品別',
};

const D302Form: React.FC = () => {
  const { loading, data, unitSummary, groupCounts, queried, noData, runQuery, reset } = useD302Query();

  const [reportType, setReportType] = useState<string>('order_no');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      vendor_from: vendorFrom.trim() || undefined,
      vendor_to: vendorTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
      dept_from: deptFrom.trim() || undefined,
      dept_to: deptTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportType('order_no');
    setDateFrom('');
    setDateTo('');
    setVendorFrom('');
    setVendorTo('');
    setProductFrom('');
    setProductTo('');
    setDeptFrom('');
    setDeptTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const title = `繳庫單明細表（${reportTypeLabels[reportType] || reportType}）`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr><th>單號</th><th>入庫日期</th><th>廠商代號</th><th>廠商名稱</th><th>產品代號</th><th>單位</th><th>數量</th><th>單價</th><th>金額</th><th>部門</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.FD120_01}</td><td>${r.FD120_11}</td><td>${r.FD120_12}</td><td>${r.vendor_name}</td><td>${r.product_code}</td><td>${r.FD121_16}</td><td class="right">${r.FD121_15}</td><td class="right">${r.FD121_17?.toFixed(2)}</td><td class="right">${r.FD121_18?.toFixed(2)}</td><td>${r.dept_name || r.FD120_17}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '單號', dataIndex: 'FD120_01', key: 'FD120_01', width: 110 },
    { title: '入庫日期', dataIndex: 'FD120_11', key: 'FD120_11', width: 100 },
    { title: '廠商代號', dataIndex: 'FD120_12', key: 'FD120_12', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true, width: 150 },
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', width: 130 },
    { title: '單位', dataIndex: 'FD121_16', key: 'FD121_16', width: 60 },
    { title: '數量', dataIndex: 'FD121_15', key: 'FD121_15', width: 90, align: 'right' as const },
    { title: '單價', dataIndex: 'FD121_17', key: 'FD121_17', width: 90, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '金額', dataIndex: 'FD121_18', key: 'FD121_18', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '部門', key: 'dept', width: 100, render: (_: unknown, r: ReportRow) => r.dept_name || r.FD120_17 },
  ];

  const summaryColumns: ColumnsType<UnitSummary> = [
    { title: '單位', dataIndex: 'FD121_16', key: 'FD121_16', width: 80 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const },
    { title: '合計金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D302} action="query">
      <div className="d302-container">
        <div className="d302-header">
          <h2>繳庫單明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="order_no">單據別</Radio.Button>
              <Radio.Button value="date">日期別</Radio.Button>
              <Radio.Button value="product">產品別</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('入庫日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('入庫日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('廠商代號起')}
              <Input value={vendorFrom} onChange={e => setVendorFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('廠商代號迄')}
              <Input value={vendorTo} onChange={e => setVendorTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號起')}
              <Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號迄')}
              <Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號起')}
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號迄')}
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {groupCounts.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                廠商筆數：{groupCounts.map(g => `${g.name || g.code}(${g.count})`).join('、')}
              </div>
            )}
            <Table<ReportRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.FD120_01}-${r.product_code}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1100 }}
              style={{ marginBottom: 16 }}
            />
            {unitSummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>單位合計</div>
                <Table<UnitSummary>
                  columns={summaryColumns}
                  dataSource={unitSummary}
                  rowKey="FD121_16"
                  size="small"
                  pagination={false}
                  scroll={{ x: 500 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default D302Form;
